import { useEffect, useState, useRef } from 'react'
import { withRouter } from 'react-router'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { useQuery } from 'Hooks'
import { connect } from 'react-redux'
import Feather from '@sysunite/react-feather'
import { QuestionCircleOutlined, SaveOutlined } from '@ant-design/icons'
import AceEditor from 'react-ace'
import { ResizableBox } from 'react-resizable'
import { Button, Tooltip, message, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { WeaverQueryHelperModal } from 'Components'
import { getNamespaces, getNamespaceValue } from 'Services/WeaverUtils'
import { setCurrentItem, setTab, setTabDisabled } from 'Actions/explore'
import { useHotKeys } from 'Hooks'
import { QUERY } from 'Types'
import "ace-builds/src-noconflict/mode-perl"
import "ace-builds/src-noconflict/theme-solarized_light"
import weaverQueryCompleter from 'Components/weaverQueryCompleter'
import QueryTable from './QueryTable'
import SparqlAnalyze from './SparqlAnalyze'
import './GraphConsole.scss'

const defaultQuery = `
  # Gets all elements with an outgoing 'type' relation, fully loads all adjacent nodes.
  # (both incoming, and outgoing relations are loaded).

  hasRelationOut 'type'
  selectOut '*'
  selectIn '*'
  limit 50
`

const defaultSparql = `
  # Fully loads all nodes.

  SELECT DISTINCT ?node
  WHERE {
    ?node ?key ?to
  }
`

const loadingIcon = <LoadingOutlined style={{ fontSize: 44 }} spin />

const GraphConsole = ({
  currentBranch,
  openInConsole,
  // Default ant sider width (when non resizable)
  left = 249,
  fullWidth,
  setCurrentItem,
  setTab,
  setTabDisabled,
  tabIsLocked,
  darkModeEnabled,
  sparqlOnly = false,
  saveQueryAsView = true
}) => {

  const defaultConsoleQuery = {
    type: sparqlOnly ? 'Sparql' : 'QueryBuilder',
    query: sparqlOnly ? defaultSparql : defaultQuery,
    mode: 'find'
  }

  const editorRef = useRef()
  const tabIsLockedRef = useRef(tabIsLocked)
  const params = useQuery()
  const history = useHistory()
  const { url } = useRouteMatch()
  const { hash }  = useLocation()

  const [isOpen, setIsOpen] = useState(false)
  const [height, setHeight] = useState(200)
  const [consoleQuery, setConsoleQuery] = useState(defaultConsoleQuery)
  const [firedQuery, setFiredQuery] = useState(null)
  const [attrKeys, setAttrKeys] = useState([])
  const [relKeys, setRelKeys] = useState([])
  const [namespaceValues, setNamespaceValues] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [helperIsVisible, setHelperIsVisible] = useState(false)
  const iconType = isOpen ? 'x' : 'terminal'

  const width = fullWidth - left

  useEffect(() => {
    tabIsLockedRef.current = tabIsLocked
  }, [tabIsLocked])

  useEffect(() => {
    if (openInConsole) {
      setIsOpen(true)
      setConsoleQuery(openInConsole)
      fireQuery(openInConsole.query)
    }
  }
  , [openInConsole])

  useEffect(() => {
    const setData = async () => {
      const relKeyBindings = await Weaver.MetaQueries.relationsCount({ limit: 99999, branch: currentBranch })
      setRelKeys(relKeyBindings.map(({ key }) => key ))
      const attrKeyBindings = await Weaver.MetaQueries.attributesCount({ limit: 99999, branch: currentBranch })
      setAttrKeys(attrKeyBindings.map(({ key }) => key ))
      setNamespaceValues(Object.values(getNamespaces()))
    }
    setData()
    setIsLoading(false)
  }, [])

  useEffect(() => {
    if (isOpen) {
      editorRef.current?.editor.focus()
    } else {
      editorRef.current?.editor.blur()
    }
  }
  , [isOpen])

  useHotKeys([{
    sequence: 'esc',
    fn: () => setIsOpen(false),
    expr: isOpen,
    temporary: true,
    watchers: [isOpen]
  }, {
    sequence: 'mod+d',
    fn: () => setIsOpen(!isOpen),
    temporary: true,
    watchers: [isOpen]
  }])

  const switchQueryType = () => {
    if (consoleQuery.type === 'QueryBuilder') {
      setConsoleQuery({ type: 'Sparql', query: defaultSparql, mode: 'find' })
    } else {
      setConsoleQuery({ type: 'QueryBuilder', query: defaultQuery, mode: 'find' })
    }
  }

  const getCurrentTimeInMillis = () => new Date().getMilliseconds()

  const getValidQuery = async (q) => {
    const { query, type } = q
    if (type === 'Sparql') {
      await new Weaver.Sparql(query).limit(0).findNodes()
      return new Weaver.Sparql(query)
    } else {
      return new Weaver.QueryBuilder(query, (item) => getNamespaceValue(item)).build()
    }
  }

  const getEditorValues = (editor) => {
    const mode = editor.getOption('mode').split('/')[2]
    const type = editor.getOption('mode').split('/')[2] === 'sparql' ? _.startCase(mode) : 'QueryBuilder'
    return { type, query: editor.getValue()}
  }

  const fireQuery = async (q, mode='find') => {
    try {
      consoleQuery.mode = mode
      consoleQuery.type === 'Sparql' && setFiredQuery(q.query)
      const validatedQ = await getValidQuery(q)
      setCurrentItem({
        type: QUERY,
        ranAt: getCurrentTimeInMillis(),
        data: {
          query: validatedQ
        }
      })
      setTabDisabled({ key: 'elements', disabled: false })
      setTabDisabled({ key: 'attributes', disabled: true })
      params.set('tab', 'elements')
      history.replace({ pathname: url, search: params.toString(), hash })
      !tabIsLockedRef.current ? setTab('elements') : null
    } catch (e) {
      console.error(e)
      message.error('Your query is badly formed. Click the help icon to see the query reference.')
    }
  }

  const renderQueryItems = () => {
    if (consoleQuery.type === 'QueryBuilder' || !isOpen) {
      return null;
    }

    if (consoleQuery.mode === 'analyze') {
      return (
        <SparqlAnalyze
          height={height}
          width={width}
          query={firedQuery}
        />
      );
    }

    return (
      <QueryTable
        height={height}
        width={width}
        query={firedQuery}
      />
    );
  }

  const renderConsoleButtons = () => (
    <>
      {
        !sparqlOnly
        &&
        <Tooltip title={consoleQuery.type} placement="left">
          <Button
            data-testid="query-language-switcher-button"
            icon={consoleQuery.type === 'Sparql' ? 'S' : 'Q'}
            style={{ pointerEvents: 'auto', zIndex: 9999 }}
            onClick={() => switchQueryType()}
          />
        </Tooltip>

      }
      <Tooltip title="Help with queries" placement="left">
        <Button
          data-testid="query-helper-button"
          icon={<QuestionCircleOutlined />}
          style={{ pointerEvents: 'auto', zIndex: 9999 }}
          onClick={() => setHelperIsVisible(true)}
        />
      </Tooltip>
      {
        saveQueryAsView
        &&
        <Tooltip title="Save query as view" placement="left">
          <Button
            data-testid="save-query-button"
            icon={<SaveOutlined />}
            style={{ pointerEvents: 'auto', zIndex: 9999 }}
            onClick={() => {
              const { current: { editor } } = editorRef
              const { query, type } = getEditorValues(editor)
              history.push(`${url}/settings/views/new?viewType=graphView&queryType=${type}&query=${encodeURIComponent(query)}`)
            }}
          />
        </Tooltip>
      }
      <div style={{ flex: '1 1 auto' }} />
      <Tooltip
        title="Run query (Cmd/Ctrl + Enter)"
        placement="left"
      >
        <Button
          data-testid="fire-console-query-button"
          icon={<Feather type='corner-down-left' />}
          style={{ pointerEvents: 'auto', zIndex: 9999 }}
          onClick={(e) => {
            const { current: { editor } } = editorRef
            const query = getEditorValues(editor)
            if (e.altKey) {
              fireQuery(query, 'analyze')
            } else {
              fireQuery(query)
            }
          }}
        />
      </Tooltip>
    </>
  )
  return (
    <div
      style={{ left, width }}
      className="graph-console-container"
    >
      <div
        style={{ height: isOpen ? height : null }}
        className="console-button-container"
      >
        <Tooltip
          title={isOpen ? 'Close (Esc)' : 'Open graph console (Cmd/Ctrl + D)'}
          placement="left"
        >
          <Button
            data-testid="open-close-graph-console"
            icon={<Feather size="1em" type={iconType} />}
            style={{ pointerEvents: 'auto', zIndex: 9999 }}
            onClick={() => setIsOpen(!isOpen)}
          />
        </Tooltip>
        { isOpen && renderConsoleButtons() }
      </div>
      { renderQueryItems() }
      <ResizableBox
        height={height}
        width={width}
        minConstraints={[width, 50]}
        maxConstraints={[width, 500]}
        onResize={(event, { size }) => setHeight(size.height) }
        style={{
          display: isOpen || 'none',
          position: 'absolute',
          bottom: 0,
          pointerEvents: 'auto'
        }}
        handle={
          <div
            style={{
              width: '100%',
              height: 3,
              cursor: 'ns-resize',
              position: 'absolute',
              top: -3
            }}
          />
        }
        handleSize={[width, 15]}
        resizeHandles={['n']}
      >
        <div style={{ top: (isOpen ? -53 : 7), height, width }}>
          {
            isLoading
            ?
              <div
                style={{
                  backgroundColor: darkModeEnabled && '#002B36' || '#FDF6E3',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                <Spin indicator={loadingIcon} style={{ display: 'flex', alignItems: 'center' }} />
              </div>
            :
              <AceEditor
                placeholder="Enter your query here"
                mode={consoleQuery.type === 'QueryBuilder' ? 'perl' : 'sparql'}
                theme={darkModeEnabled && "solarized_dark" || "solarized_light"}
                name="Graph view devtools"
                fontSize={16}
                showGutter={true}
                highlightActiveLine={true}
                value={consoleQuery.query}
                onChange={(e) => setConsoleQuery({ ...consoleQuery, query: e })}
                height={(height) + 'px'}
                width={width + 'px'}
                ref={editorRef}
                setOptions={{
                  showPrintMargin: false,
                  enableBasicAutocompletion: [weaverQueryCompleter(attrKeys, relKeys, namespaceValues)],
                  enableLiveAutocompletion: [weaverQueryCompleter(attrKeys, relKeys, namespaceValues)],
                  showLineNumbers: true,
                  tabSize: 2,
                }}
                commands={[{
                  name: 'runQuery',
                  bindKey: { win: 'Ctrl-enter', mac: 'Command-enter' },
                  exec: async (editor) => {
                    const current = await Weaver.getCoreManager().currentBranch
                    params.set('branch', current.id())
                    fireQuery(getEditorValues(editor))
                  }
                }, {
                  name: 'toggleDevtools',
                  bindKey: { win: 'Ctrl-d', mac: 'Command-d' },
                  exec: () => setIsOpen(false)
                }, {
                  name: 'hideDevtools',
                  bindKey: { win: 'esc', mac: 'esc' },
                  exec: () => setIsOpen(false)
                }]}
                style={{ borderTop: '1px solid var(--borderColor)'}}
              />
          }
        </div>
      </ResizableBox>
      <WeaverQueryHelperModal
        queryType={consoleQuery.type}
        visible={helperIsVisible}
        setVisible={setHelperIsVisible}
      />
    </div>
  )
}

const mapStateToProps = ({ branches, explore }) => (
  {
    currentBranch: branches.currentBranch,
    fullWidth: explore.width,
    tabIsLocked: explore.tabIsLocked,
    openInConsole: explore.openInConsole,
    darkModeEnabled: explore.darkModeEnabled
  }
)

export default connect(
  mapStateToProps,
  { setCurrentItem, setTab, setTabDisabled }
)(withRouter(GraphConsole))
